import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { ImageItem } from "@app/types";
import { useState } from "react";
import clsx from "clsx";
import { IcArrowLeftRight, IcX } from "../icons";
import { ispClass, ispKey, splitWans, wanPriceText } from "./wan.util";

const ALL = "Tất cả";

function WanModal({ portalDialogRef, portalData }: PortalDialogProps) {
  const image: ImageItem = portalData;

  const [isp, setIsp] = useState(ALL);
  const [selected, setSelected] = useState("");

  // Logic như bản cũ: xác nhận rồi trả về chuỗi wan gốc trong ssh.router
  // Chọn là thực hiện luôn, không hỏi lại bằng hộp xác nhận
  const handleClickWan = (wan: string) => {
    portalDialogRef.close({ wan });
  };

  const wans = splitWans(image?.ssh?.router);
  const isps = [ALL, ...Array.from(new Set(wans.map((wan) => ispKey(wan.isp)).filter(Boolean)))];
  const list = wans.filter((wan) => isp === ALL || ispKey(wan.isp) === isp);
  const picked = wans.find((wan) => wan.raw === selected);

  return (
    <Modal onCancel={portalDialogRef.close} buttonCancelInChildren={false}>
      <div className="flex max-h-[min(86vh,720px)] w-[min(560px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start gap-3 border-b border-line px-[18px] pb-3 pt-4">
          <h2 className="flex flex-1 flex-col gap-0.5 text-[17px] font-bold">
            Chọn đường truyền Internet
            {image?.name && <small className="text-[12.5px] font-medium text-muted">{image.name}</small>}
          </h2>
          <button
            type="button"
            aria-label="Đóng"
            className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-line bg-surface text-fg hover:bg-surface-2"
            onClick={() => portalDialogRef.close()}
          >
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-[18px] pb-[18px] pt-4">
          {!wans.length ? (
            <p className="m-0 rounded-xl bg-surface-2 px-4 py-3 text-muted">Máy này chưa có đường truyền để chọn.</p>
          ) : (
            <>
              {isps.length > 2 && (
                <div className="flex flex-wrap gap-2">
                  {isps.map((name) => (
                    <button
                      key={name}
                      type="button"
                      aria-pressed={isp === name}
                      onClick={() => setIsp(name)}
                      className={clsx(
                        "rounded-full border px-3.5 py-1.5 text-[13px] font-medium",
                        isp === name ? "border-fg bg-fg text-surface" : "border-line bg-surface text-muted hover:text-fg"
                      )}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 max-[560px]:grid-cols-2" role="radiogroup" aria-label="Đường truyền">
                {list.map((wan) => {
                  const on = selected === wan.raw;

                  return (
                    <button
                      key={wan.raw}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      title={wan.raw.trim()}
                      onClick={() => setSelected(wan.raw)}
                      className={clsx(
                        "flex flex-col items-start gap-0.5 rounded-[10px] border bg-surface px-3 py-2.5 text-left",
                        on
                          ? "border-brand shadow-[0_0_0_3px_var(--brand-soft)]"
                          : "border-line hover:border-brand-line hover:bg-brand-soft"
                      )}
                    >
                      <b className="text-sm">{wan.code}</b>
                      {wan.isp && (
                        <span className={clsx("rounded-[5px] px-1.5 text-[11px] font-semibold", ispClass(wan.isp))}>
                          {wan.isp}
                        </span>
                      )}
                      {wan.price !== null && (
                        <span className="text-[12.5px] font-semibold tabular-nums">{wanPriceText(wan.price)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-[18px] py-3 text-[13px] text-muted">
          <span>
            {picked ? (
              <>
                Đổi sang{" "}
                <b className="text-fg">
                  {picked.code}
                  {picked.isp && ` · ${picked.isp}`}
                </b>
                {picked.price !== null && (
                  <>
                    {" "}
                    · <b className="tabular-nums text-fg">{wanPriceText(picked.price)}</b>
                  </>
                )}
              </>
            ) : (
              "Chọn đường truyền mới."
            )}
          </span>
          <button
            type="button"
            disabled={!picked}
            className="inline-flex h-10 items-center gap-2 rounded-[9px] bg-brand px-4 font-semibold text-brand-ink hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => picked && handleClickWan(picked.raw)}
          >
            <IcArrowLeftRight className="h-[18px] w-[18px]" />
            Đổi đường truyền
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default WanModal;
